import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion';
import { MailIcon, RefreshIcon, CheckCircleIcon } from '@heroicons/react/outline'; // Using outline icons

const VerifyEmailPage = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [isSending, setIsSending] = useState(false); // State for resend loading
  const [resendError, setResendError] = useState(null); // State for resend error
  const [resendSuccess, setResendSuccess] = useState(false); // State for resend success message

  // Function to resend verification email (calls YOUR backend)
  const handleResendVerification = async () => {
    setIsSending(true);
    setResendError(null);
    setResendSuccess(false);

    try {
      // 1. Get the user's access token to authenticate the backend request
      const accessToken = await getAccessTokenSilently();

      // 2. Call your backend API endpoint
      const response = await fetch('/api/resend-verification', { // Replace with your actual backend endpoint
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Include the access token in the Authorization header
          'Authorization': `Bearer ${accessToken}`,
        },
        // You might not need to send user.sub if your backend can get it from the token
        // body: JSON.stringify({ userId: user.sub })
      });

      if (!response.ok) {
        // Try to get error message from backend response
        let errorMsg = 'Failed to resend verification email.';
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorMsg;
        } catch (_) { }
        throw new Error(errorMsg);
      }

      // If successful
      setResendSuccess(true);
      // alert('Verification email sent! Please check your inbox.'); // Optional: use state instead
    } catch (err) {
      console.error('Error resending verification:', err);
      setResendError(err.message || 'An unexpected error occurred.');
      // alert(`Error: ${err.message}`); // Optional: use state instead
    } finally {
      setIsSending(false);
    }
  };

  // Function to check verification status (uses reload)
  const handleCheckVerification = async () => {
    setIsChecking(true);
    setCheckError(null);
    try {
      await getAccessTokenSilently({ ignoreCache: true });
      window.location.reload();
    } catch (error) {
      console.error('Error checking verification status:', error);
      let errorMessage = 'Could not refresh status. Please try logging out and back in.';
      if (error.error === 'login_required' || error.error === 'consent_required') {
        errorMessage = 'Your session may have expired. Please log out and log back in.';
      }
      setCheckError(errorMessage);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 via-red-50 to-pink-50 dark:from-gray-800 dark:via-gray-900 dark:to-black px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white dark:bg-gray-800 p-8 md:p-12 rounded-xl shadow-2xl max-w-lg w-full text-center border border-gray-100 dark:border-gray-700"
      >
        <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-6">
          <MailIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">Verify Your Email Address</h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Thank you for signing up! A verification link has been sent to your email address:
          <strong className="block my-2 text-gray-800 dark:text-gray-100">{user?.email || 'your email'}</strong>
          Please click the link in the email to verify your account and gain full access.
        </p>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          If you haven't received the email after a few minutes, please check your spam folder or try resending.
        </p>

        {/* Resend Verification Button */}
        <button
          onClick={handleResendVerification}
          disabled={isSending}
          className="mb-4 w-full flex items-center justify-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-yellow-800 dark:text-yellow-200 bg-yellow-400 hover:bg-yellow-500 dark:bg-yellow-600 dark:hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSending ? (
            <><RefreshIcon className="animate-spin h-5 w-5 mr-2" /> Sending...</>
          ) : (
            <><RefreshIcon className="h-5 w-5 mr-2" /> Resend Verification Email</>
          )}
        </button>
        {/* Resend Feedback Messages */}
        {resendSuccess && <p className="text-green-600 dark:text-green-400 text-sm mb-4">Verification email sent successfully! Please check your inbox.</p>}
        {resendError && <p className="text-red-500 text-sm mb-4">{resendError}</p>}

        {/* Check Verification Button */}
        <button
          onClick={handleCheckVerification}
          disabled={isChecking}
          className="mb-6 w-full flex items-center justify-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isChecking ? (
            <><RefreshIcon className="animate-spin h-5 w-5 mr-2" /> Checking...</>
          ) : (
            <div className='flex items-center' onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
              <CheckCircleIcon className="h-5 w-5 mr-2" /> I've Verified My Email
            </div>
          )}
        </button>
        {/* Check Status Error Message */}
        {checkError && <p className="text-red-500 text-sm mb-4">{checkError}</p>}

        {/* Log Out Button */}
        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          className="w-full px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
        >
          Log Out
        </button>

      </motion.div>
    </div>
  );
};

export default VerifyEmailPage; 